<template>
  <v-container>
    <v-row no-gutters>
      <v-col
        cols="12"
        md="10"
        xl="10"
      >
        <v-text-field
          v-model="search"
          class="pl-7"
          placeholder="Search By Task Content"
          append-icon="search"
          label="Search Task"
          hide-no-data
          hide-selected
          clearable
        />
      </v-col>
      <v-spacer />
      <v-col
        cols="12"
        md="1"
        xl="1"
        align-self="center"
      >
        <task-dialog-create-on-behalf
          :employee-uid="employeeUid"
          @created="loadGivenTask"
        />

        <v-tooltip
          content-class="bottom"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-on="on"
              @click="loadGivenTask"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Reload</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        class="py-0 d-flex"
      >
        <p class="font-weight-bold font-17 my-auto pr-2">
          Filters:
        </p>
        <v-chip-group
          v-model="statusFilterId"
          mandatory
          active-class="deep-purple--text text--accent-4"
          column
        >
          <v-chip
            v-for="item in status"
            :key="item.id"
            outlined
            :value="item.id"
          >
            {{ item.name }}
            <v-avatar
              v-show="item.hide"
              color="red"
              size="7"
              class="mb-5 avatar-size"
            />
          </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-data-table
      :headers="givenTaskHeader"
      :items="givenTasks"
      item-key="name"
      hide-default-footer
      height="370px"
      class="assignee-task-table"
      :loading="loading"
    >
      <template v-slot:item.task.content="{ item }">
        <div>
          <span
            :class="{
              'two-lines ellipse profile-content-width':isProfileTab,
              'two-lines ellipse approval-content-width':!isProfileTab
            }"
          > 
            {{ item.task.content }}
          </span>
        </div>
      </template> 

      <template v-slot:item.task.owner.person.name="{ item }">
        <div>
          <span
            :class="{
              'two-lines ellipse profile-name-width':isProfileTab,
              'two-lines ellipse approval-name-width':!isProfileTab
            }"
          > 
            {{ item.task.owner.person.name }}
          </span>
        </div>
      </template> 

      <template v-slot:item.task_status.name="{ item }">
        <div>
          <span
            :class="{
              'warning--text two-lines ellipse profile-status-width': (item.task_status.id === 100 && isProfileTab) || (item.task_status.id === 100 && !isProfileTab),
              'green--text two-lines ellipse profile-status-width': (item.task_status.id === 199 && isProfileTab) || (item.task_status.id === 199 && !isProfileTab),
              'black--text two-lines ellipse profile-status-width': (item.task_status.id === 200 && isProfileTab) || (item.task_status.id === 200 && !isProfileTab),
              'red--text two-lines ellipse profile-status-width':(item.task_status.id === 501 && isProfileTab) || (item.task_status.id === 501 && !isProfileTab),
            }"
          >
            {{ item.task_status.name }}
          </span>
        </div>
      </template> 

      <template v-slot:item.task.priority.name="{ item }">
        <div>
          <span
            :class="{
              'two-lines ellipse profile-priority-width':isProfileTab,
              'two-lines ellipse approval-priority-width': !isProfileTab
            }"
          >
            {{ item.task.priority.name }}
          </span>
        </div>
      </template>
      
      <template v-slot:item.task.task_category.name="{ item }">
        <div>
          <span
            :class="{
              'two-lines ellipse profile-category-width':isProfileTab,
              'two-lines ellipse approval-category-width': !isProfileTab
            }"
          >
            {{ item.task.task_category.name }}
          </span>
        </div>
      </template>

      <template v-slot:item.task.due_date="{ item }">
        <div>
          <span v-if="item.task.due_date">
            {{ $moment(item.task.due_date).format('DD-MM-YYYY') }}
          </span>

          <span v-else>
            N/A
          </span>
        </div>
      </template>

      <template v-slot:item.task.created_at="{ item }">
        <div>
          <span>
            {{ $moment(item.task.created_at).format('DD-MM-YYYY') }}
          </span>
        </div>
      </template>

      <template v-slot:item.action="{ item }"> 
        <task-dialog-complete
          v-if="![200,501].includes(item.task_status.id)"
          :task="item"
          @updated="loadGivenTask"
        />

        <task-dialog-assignee-view
          v-else
          :task="item"
        />
      </template>
    </v-data-table>
    <v-container
      v-if="finishLoading && givenTasks.length>0"
      class="text-right"
    >
      <v-btn
        icon
        color="primary"
        :disabled="pageNumber === 1"
        @click="previousPage"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span class="pagination">{{ from }}-{{ to }} of {{ total }}</span>
      <v-btn
        icon
        color="primary"
        :disabled="pageNumber === lastPage"
        @click="nextPage"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-container>
  </v-container>
</template>

<script>
  export default {
    components: {
      TaskDialogComplete: () => import('@/components/task/DialogComplete'),
      TaskDialogAssigneeView: () => import('@/components/task/DialogAssigneeView'),
      TaskDialogCreateOnBehalf: () => import('@/components/task/DialogCreateOnBehalf'),
    },
    props: {
      employeeUid: {
        type: String,
        required: true,
      },
      isProfileTab: {
        type: Boolean,
      },
    },
    data () {
      return {
        loading: false,
        search: '',
        givenTasks: [],
        givenTaskHeader: [
          { 
            text: 'Task',
            sortable: false,
            value: 'task.content',
            align: 'left',
          },
          { 
            text: 'Assigned by',
            value: 'task.owner.person.name',
            align: 'center',
          },
          { 
            text: 'Status',
            align: 'center',
            value: 'task_status.name',
          },
          { 
            text: 'Priority',
            align: 'center',
            value: 'task.priority.name',
          },
          { 
            text: 'Category',
            align: 'center',
            value: 'task.task_category.name',
          },
          { 
            text: 'Due Date',
            value: 'task.due_date',
            align: 'center',
            width: 140,
          },
          { 
            text: 'Created On',
            align: 'center',
            value: 'task.created_at',
            width: 140,
          },
          {
            text: '',
            sortable: false,
            align: 'center',
            value: 'action',
          },
        ],
        statusFilterId: 100,
        status: [],
        pageNumber: 0,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 0,
        finishLoading: false,
        pendingCounter: false,
      }
    },

    watch: {
      statusFilterId: function (value) {
        if (value) {
          this.loadGivenTask()
        }
      },

      search: function (value) {
        if (value) {
          this._searchTimerId = setTimeout(() => {
            if (value) {
              this.loadGivenTask()
            } else {
              this.loadGivenTask()
            }
          }, 1000) /* 1000ms throttle */
        }
      },
    },

    created () {
      this.loadApprovalStatus()
      this.loadGivenTask()
    },
    
    methods: {
      previousPage () {
        this.loading = true
        this.$api.assigneeTasks().list(this.employeeUid, '?page=' + (this.pageNumber - 1), this.prepareFormData())
          .then((response) => {
            this.givenTasks = []
            this.setPaginationDetails(response.data)
            this.getAssigneeTaskStatus(response.data.data)
            this.finishLoading = true
            this.loading = false
          })
      },

      nextPage () {
        this.loading = true
        this.$api.assigneeTasks().list(this.employeeUid, '?page=' + (this.pageNumber + 1), this.prepareFormData())
          .then((response) => {
            this.givenTasks = []
            this.setPaginationDetails(response.data)
            this.getAssigneeTaskStatus(response.data.data)
            this.finishLoading = true
            this.loading = false
          })
      },

      loadApprovalStatus () {
        Promise.all([
          this.$api.approvalStatus().list(),
          this.$api.assigneeTasks().pendingTaskCount(this.employeeUid),
        ]).then(([approvalStatus, assigneePendingCount]) => {
          this.pendingCounter = assigneePendingCount.data

          this.status = approvalStatus.data.filter(item => {
            if (item.id === 200) {
              item.name = 'Closed'
              item.hide = false
            }
            if (item.id === 204) {
              item.id = 199
              item.hide = false
            }
            if (item.id === 100) {
              item.hide = this.pendingCounter
            }
            if (item.id === 501) {
              item.hide = false
            }

            return item.id !== 502 && item.id !== 201 && item.id !== 500
          })
        })
      },

      loadGivenTask () {
        this.loading = true

        this.$api.assigneeTasks().list(this.employeeUid, '', this.prepareFormData())
          .then((response) => {
            this.givenTasks = []
            this.setPaginationDetails(response.data)
            this.getAssigneeTaskStatus(response.data.data)
            this.finishLoading = true
            this.loading = false
          })
      },
      getAssigneeTaskStatus (tasks) {
        let temp = []
        tasks.map(task => {
          const taskStatus = task.task.approval_request.approvals.length > 0 ? _.orderBy(task.task.approval_request.approvals, ['created_at'], ['desc'])[0].status : { id: 100, name: 'Pending' }
          const assigneeName = this.getAssigneeName(task.task.assignees)
          const viewerName = this.getViewerName(task.task.viewers)

          temp.push({ 
            ...task, 
            task_status: taskStatus,
            assignee_names: assigneeName,
            viewer_names: viewerName,
          })
        })
        temp.map(task => {
          if (task.task_status.id === 502) { // to show pending status
            task.task_status.id = 100
            task.task_status.name = 'Pending'
          } else if (task.task_status.id === 204) {
            task.task_status.id = 199 // for sorting according to status
          } else if (task.task_status.id === 200) {
            task.task_status.name = 'Closed'
          }
     
          task.comment_scroll_id = task.id + '-' + task.assignee_uid + '-comment-scroll'
          task.approval_scroll_id = task.id + '-' + task.assignee_uid + '-approval-scroll'
          task.task_scroll_id = task.id + '-' + task.assignee_uid + '-task-scroll'
        })

        this.givenTasks = _.orderBy(temp, ['task_status.id', 'created_at'], ['asc', 'desc'])
      },

      getAssigneeName (assignees) {
        let name = ''
        assignees.map(assignee => {
          name += assignee.employee.person.name + ', '
        })
        return name.substring(0, name.length - 2)
      },

      getViewerName (viewers) {
        let name = ''
        viewers.map(viewer => {
          name += viewer.employee.person.name + ', '
        })
        return name.substring(0, name.length - 2)
      },
      setPaginationDetails (page) {
        this.pageNumber = page.current_page
        this.total = page.total
        this.from = page.from
        this.to = page.to
        this.lastPage = page.last_page
      },
      prepareFormData () {
        return {
          filter: this.statusFilterId,
          searchInput: this.search,
        }
      },
    },
  }
</script>

<style scoped>
.two-lines {
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}
.ellipse{
  overflow: hidden;
  text-overflow: ellipsis;
}
.profile-content-width{
  width:200px;
}
.approval-content-width{
  width:350px;
}
.profile-name-width{
  width:140px;
}
.approval-name-width{
  width:120px;
}
.profile-status-width{
  width:130px;
}
.profile-priority-width{
  width:80px;
}
.approval-priority-width{
  width:80px;
}
.approval-category-width{
  width:100px;
}
.profile-category-width{
  width:100px;
}
.font-17{
  font-size: 17px;
}
.pagination {
  font-size: 12px;
  color: #5f6368;
}
.assignee-task-table >>> ::-webkit-scrollbar {
  height: 6px; 
  width: 3px !important; 
}
/* Track */
.assignee-task-table >>> ::-webkit-scrollbar-track {
  box-shadow: inset 0 0 2px #e6e6e6; 
  border-radius: 10px;
}
/* Handle */
.assignee-task-table >>> ::-webkit-scrollbar-thumb {
  background: grey; 
  border-radius: 10px;
  height: 2px;
}
/* Handle on hover */
.assignee-task-table >>> ::-webkit-scrollbar-thumb:hover {
  background:  #555; 
}
.avatar-size{
  height: 7px !important;
  min-width: 7px !important;
  width: 7px !important;
}
</style>
